'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { TournamentPublicLayout } from '@/components/Layout/TournamentPublicLayout';
import { PageLoading } from '@/components/PageLoading';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Users,
  Trophy,
  Calendar,
  Swords,
  DollarSign,
  Clock,
  AlertCircle,
  Eye,
  Gamepad2,
  LayoutGrid,
  BarChart3,
} from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { setAuthRedirect } from '@/lib/auth-token';
import { apiGet } from '@/lib/api';
import { cn } from '@/lib/utils';
import { PublicRegisterTeam } from '../components/PublicRegisterTeam';
import { TournamentTeamsList } from '../components/TeamRosterCard';
import BracketView from '../components/BracketView';
import DoubleBracketView from '../components/DoubleBracketView';
import {
  TournamentRulesViewer,
  hasTournamentRules,
} from '../components/TournamentRulesViewer';
import { TournamentSocialLinksDisplay } from '../components/TournamentSocialLinksDisplay';
import { PrizeDistributionDisplay } from '../components/PrizeDistributionDisplay';
import MatchStatsModal from '../components/MatchStatsModal';
import type {
  Tournament,
  TournamentTeam,
  TournamentMatch,
  TournamentBracket,
} from '../interfaces/tournaments';
import {
  TOURNAMENT_STATUS_LABELS,
  TOURNAMENT_FORMAT_LABELS,
  GAME_TYPE_LABELS,
  MATCH_STATUS_LABELS,
  formatPrizePool,
} from '../interfaces/tournaments';
import TournamentStatsTab from '../components/TournamentStatsTab';

type TabType = 'info' | 'bracket' | 'teams' | 'matches' | 'stats';

const TAB_QUERY_KEY = 'tab';

function isTabAvailable(
  tab: TabType,
  tournament: Tournament,
  hasBracket: boolean,
  isDraftPreview = false,
): boolean {
  if (tab === 'bracket' || tab === 'matches') return hasBracket;
  if (tab === 'stats') return tournament.game_type === 'valorant' && !isDraftPreview;
  return tab === 'info' || tab === 'teams';
}

function resolveTabFromUrl(
  raw: string | null,
  tournament: Tournament,
  hasBracket: boolean,
  isDraftPreview = false,
): TabType {
  const validTabs: TabType[] = ['info', 'bracket', 'teams', 'matches', 'stats'];
  if (raw && validTabs.includes(raw as TabType)) {
    const tab = raw as TabType;
    if (isTabAvailable(tab, tournament, hasBracket, isDraftPreview)) return tab;
  }
  return 'info';
}

type TournamentDetailPayload = Tournament & {
  teams?: TournamentTeam[];
  matches?: TournamentMatch[];
};

type PublicTournamentDetailProps = {
  slug: string;
  initialData?: TournamentDetailPayload;
  isDraftPreview?: boolean;
};

const STATUS_COLOR_MAP: Record<Tournament['status'], string> = {
  draft: 'bg-muted/80 text-muted-foreground border-border',
  registration: 'bg-primary/25 text-primary border-primary/50 glow-primary-sm',
  in_progress: 'bg-primary/15 text-primary border-primary/30',
  completed: 'bg-muted/80 text-muted-foreground border-border',
  cancelled: 'bg-destructive/20 text-destructive border-destructive/40',
};

function getStatusColor(status: Tournament['status']): string {
  return STATUS_COLOR_MAP[status] ?? STATUS_COLOR_MAP.draft;
}

function buildBracket(
  tournamentData: Tournament,
  teamsData: TournamentTeam[],
  matchesData: TournamentMatch[],
): TournamentBracket {
  return {
    tournament: tournamentData,
    teams: teamsData,
    matches: matchesData,
    structure: {
      winners: (() => {
        const rounds = new Map<number, TournamentMatch[]>();
        matchesData.forEach((match) => {
          const list = rounds.get(match.round) || [];
          list.push(match);
          rounds.set(match.round, list);
        });
        return [...rounds.keys()]
          .toSorted((a, b) => a - b)
          .map((round) =>
            (rounds.get(round) || []).map((match) => ({
              id: match.id,
              match,
              team1: teamsData.find((team) => team.id === match.team1_id),
              team2: teamsData.find((team) => team.id === match.team2_id),
            })),
          );
      })(),
    },
  };
}

function hydrateFromPayload(data: TournamentDetailPayload) {
  const teamsData = data.teams || [];
  const matchesData = data.matches || [];
  return {
    tournament: data,
    teams: teamsData,
    bracket: buildBracket(data, teamsData, matchesData),
  };
}

function StatChip({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex h-full min-h-[4.25rem] items-center gap-3 rounded-xl border border-primary/20 bg-card/80 px-4 py-3 backdrop-blur-sm">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-display text-sm font-semibold text-foreground truncate">{value}</p>
      </div>
    </div>
  );
}

export const PublicTournamentDetail: React.FC<PublicTournamentDetailProps> = ({
  slug,
  initialData,
  isDraftPreview = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const { isAuthenticated } = useAuth();

  const hydrated = initialData ? hydrateFromPayload(initialData) : null;
  const [tournament, setTournament] = useState<Tournament | null>(hydrated?.tournament ?? null);
  const [bracket, setBracket] = useState<TournamentBracket | null>(hydrated?.bracket ?? null);
  const [teams, setTeams] = useState<TournamentTeam[]>(hydrated?.teams ?? []);
  const [isLoading, setIsLoading] = useState(!initialData);
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    if (!hydrated?.tournament) return 'info';
    return resolveTabFromUrl(
      searchParams.get(TAB_QUERY_KEY),
      hydrated.tournament,
      Boolean(hydrated.bracket?.structure),
      isDraftPreview,
    );
  });
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<TournamentMatch | null>(null);

  useEffect(() => {
    if (initialData || !slug) return;
    loadTournamentData();
  }, [slug, initialData]);

  const hasBracket = Boolean(bracket?.structure);

  const updateTabInUrl = useCallback(
    (tab: TabType) => {
      const params = new URLSearchParams(window.location.search);
      if (tab === 'info') {
        params.delete(TAB_QUERY_KEY);
      } else {
        params.set(TAB_QUERY_KEY, tab);
      }
      const qs = params.toString();
      const newUrl = qs ? `${pathname}?${qs}` : pathname;
      window.history.replaceState({ ...window.history.state }, '', newUrl);
    },
    [pathname],
  );

  const handleTabChange = (value: string) => {
    const tab = value as TabType;
    setActiveTab(tab);
    updateTabInUrl(tab);
  };

  const loadTournamentData = async () => {
    if (!slug) return;
    setIsLoading(true);
    try {
      const endpoint = isDraftPreview
        ? `/public/tournaments/${slug}/preview`
        : `/public/tournaments/${slug}`;
      const tournamentData = await apiGet<TournamentDetailPayload>(endpoint);
      if (!tournamentData) throw new Error('Torneo no encontrado o no es público');

      const next = hydrateFromPayload(tournamentData);
      setTournament(next.tournament);
      setTeams(next.teams);
      setBracket(next.bracket);
    } catch (error) {
      console.error('Error loading tournament:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterClick = () => {
    if (!slug) return;
    const returnPath = window.location.pathname + (window.location.search || '');
    if (!isAuthenticated) {
      setAuthRedirect(returnPath);
      navigate(`/login?return=${encodeURIComponent(returnPath)}`);
      return;
    }
    setIsRegisterModalOpen(true);
  };

  const canRegister = (): boolean => {
    if (isDraftPreview) return false;
    if (!tournament) return false;
    if (tournament.status !== 'registration') return false;
    if (tournament.current_teams >= tournament.max_teams) return false;
    if (tournament.registration_deadline) {
      if (new Date() > new Date(tournament.registration_deadline)) return false;
    }
    return true;
  };

  const teamFillPercent = tournament
    ? Math.round((tournament.current_teams / tournament.max_teams) * 100)
    : 0;

  if (isLoading) {
    return (
      <TournamentPublicLayout>
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <PageLoading variant="tournament-detail" text="Cargando torneo..." minHeight="min-h-[400px]" />
        </div>
      </TournamentPublicLayout>
    );
  }

  if (!tournament) {
    return (
      <TournamentPublicLayout>
        <div className="mx-auto max-w-lg px-4 py-24 text-center sm:px-6">
          <div className="panel-solid mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-2xl">
            <AlertCircle className="h-12 w-12 text-primary" />
          </div>
          <h2 className="font-display text-3xl font-bold text-foreground mb-2">Torneo no encontrado</h2>
          <p className="text-muted-foreground mb-8">El torneo que buscas no existe o no es público</p>
          <Button onClick={() => navigate('/tournaments')} className="glow-primary">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a torneos
          </Button>
        </div>
      </TournamentPublicLayout>
    );
  }

  return (
    <TournamentPublicLayout>
      {isDraftPreview && (
        <div className="border-b border-amber-500/30 bg-amber-500/10">
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
            <Eye className="h-4 w-4 shrink-0 text-amber-400" />
            <p className="text-sm text-amber-100/90">
              <span className="font-semibold text-amber-300">Vista previa</span>
              {' — '}
              Este torneo aún no es público. Solo los miembros de la organización pueden verlo.
            </p>
          </div>
        </div>
      )}

      {/* Banner cinematográfico */}
      <div className="relative h-[220px] sm:h-[300px] lg:h-[380px] xl:h-[440px] overflow-hidden">
        {tournament.banner_url ? (
          <img 
            src={tournament.banner_url} 
            alt="" 
            loading="eager"
            className="absolute inset-0 h-full w-full object-cover" 
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/25 to-background" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
        <div
          className="absolute inset-x-0 bottom-0 h-16 bg-background"
          style={{ clipPath: 'polygon(0 40%, 100% 0, 100% 100%, 0 100%)' }}
        />

        <div className="relative mx-auto flex h-full max-w-7xl items-end px-4 pb-8 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tournaments')}
            className="absolute left-4 top-4 sm:left-6 lg:left-8 text-foreground/80 hover:text-foreground panel-solid border-0"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Torneos
          </Button>
        </div>
      </div>

      {/* Cabecera del torneo — legible, fuera del overlay */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 -mt-4 pb-8">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Badge variant="outline" className={getStatusColor(tournament.status)}>
            {TOURNAMENT_STATUS_LABELS[tournament.status]}
          </Badge>
          <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">
            <Gamepad2 className="mr-1 h-3 w-3" />
            {GAME_TYPE_LABELS[tournament.game_type]}
          </Badge>
          <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">
            <LayoutGrid className="mr-1 h-3 w-3" />
            {TOURNAMENT_FORMAT_LABELS[tournament.format]}
          </Badge>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[0.95] mb-3">
          {tournament.name}
        </h1>

        {tournament.organization && (
          <p className="text-muted-foreground mb-4">
            Organizado por{' '}
            <span className="font-semibold text-foreground">{tournament.organization.name}</span>
          </p>
        )}

        {tournament.description && (
          <p className="text-lg text-foreground/80 max-w-3xl leading-relaxed mb-6 line-clamp-3 sm:line-clamp-none">
            {tournament.description}
          </p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-stretch auto-rows-fr">
          <StatChip
            icon={Users}
            label="Equipos"
            value={`${tournament.current_teams} / ${tournament.max_teams}`}
          />
          <StatChip
            icon={Calendar}
            label="Inicio"
            value={new Date(tournament.start_date).toLocaleDateString('es-ES', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          />
          <StatChip
            icon={Clock}
            label="Cierre inscripción"
            value={
              tournament.registration_deadline
                ? new Date(tournament.registration_deadline).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                  })
                : '—'
            }
          />
          <StatChip
            icon={DollarSign}
            label="Premio"
            value={
              tournament.prize_pool != null && Number(tournament.prize_pool) > 0 ? (
                <span className="text-gradient-accent">
                  {formatPrizePool(tournament.prize_pool, tournament.prize_pool_currency)}
                </span>
              ) : (
                '—'
              )
            }
          />
        </div>
      </div>

      {/* Contenido principal + sidebar */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid gap-8 lg:grid-cols-[1fr_300px] xl:grid-cols-[1fr_320px]">
          {/* Tabs */}
          <div className="min-w-0">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="nav-segment-list mb-6">
                {(
                  [
                    { value: 'info', icon: Trophy, label: 'Info' },
                    { value: 'bracket', icon: LayoutGrid, label: 'Bracket', disabled: !hasBracket },
                    { value: 'teams', icon: Users, label: 'Equipos' },
                    { value: 'matches', icon: Swords, label: 'Partidas', disabled: !hasBracket },
                    ...(tournament.game_type === 'valorant' && !isDraftPreview
                      ? [{ value: 'stats' as const, icon: BarChart3, label: 'Estadísticas' }]
                      : []),
                  ] as Array<{
                    value: TabType;
                    icon: React.ElementType;
                    label: string;
                    disabled?: boolean;
                  }>
                ).map(({ value, icon: Icon, label, disabled }) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    disabled={disabled}
                    className="nav-segment"
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="panel-solid rounded-2xl p-6 sm:p-8">
                <TabsContent value="info" className="mt-0">
                  <div className="grid gap-4 lg:grid-cols-2 lg:items-stretch">
                    <div className="flex flex-col gap-4 h-full">
                      {tournament.description && (
                        <section className="panel-solid flex flex-1 flex-col rounded-xl p-5 sm:p-6">
                          <h3 className="font-display text-lg font-semibold text-foreground mb-3 border-l-2 border-primary pl-3">
                            Descripción
                          </h3>
                          <p className="text-foreground/75 whitespace-pre-line leading-relaxed flex-1">
                            {tournament.description}
                          </p>
                        </section>
                      )}
                      {hasTournamentRules(
                        tournament.rules,
                        tournament.rules_format,
                        tournament.rules_pdf_url,
                      ) && (
                        <section className="panel-solid flex flex-1 flex-col rounded-xl p-5 sm:p-6">
                          <h3 className="font-display text-lg font-semibold text-foreground mb-3 border-l-2 border-primary pl-3">
                            Reglas
                          </h3>
                          <div className="flex-1">
                            <TournamentRulesViewer
                              rules={tournament.rules}
                              rulesFormat={tournament.rules_format}
                              rulesPdfUrl={tournament.rules_pdf_url}
                            />
                          </div>
                        </section>
                      )}
                      {!tournament.description &&
                        !hasTournamentRules(
                          tournament.rules,
                          tournament.rules_format,
                          tournament.rules_pdf_url,
                        ) && (
                        <section className="panel-solid flex flex-1 flex-col rounded-xl p-5 sm:p-6">
                          <h3 className="font-display text-lg font-semibold text-foreground mb-3 border-l-2 border-primary pl-3">
                            Sobre el torneo
                          </h3>
                          <p className="text-muted-foreground leading-relaxed flex-1">
                            El organizador aún no ha publicado descripción ni reglas. Revisa la pestaña
                            Equipos o vuelve más tarde.
                          </p>
                        </section>
                      )}
                    </div>
                    <section className="panel-solid flex h-full flex-col rounded-xl p-5 sm:p-6 gap-5">
                      <h3 className="font-display text-lg font-semibold text-foreground border-l-2 border-primary pl-3">
                        Detalles
                      </h3>
                      <TournamentSocialLinksDisplay links={tournament.social_links} />
                      <PrizeDistributionDisplay
                        distribution={tournament.prize_distribution}
                        totalPrize={tournament.prize_pool}
                        currency={tournament.prize_pool_currency}
                      />
                      <dl className="flex flex-1 flex-col divide-y divide-primary/10 rounded-xl border border-primary/15 bg-card/50 overflow-hidden">
                        {[
                          ['Juego', GAME_TYPE_LABELS[tournament.game_type]],
                          ['Formato', TOURNAMENT_FORMAT_LABELS[tournament.format]],
                          ['Equipos máx.', String(tournament.max_teams)],
                          ['Inscritos', String(tournament.current_teams)],
                          ...(tournament.end_date
                            ? [
                                [
                                  'Finalización',
                                  new Date(tournament.end_date).toLocaleDateString('es-ES'),
                                ],
                              ]
                            : []),
                        ].map(([k, v]) => (
                          <div key={k} className="flex flex-1 items-center justify-between gap-4 px-4 py-3.5 min-h-[3rem]">
                            <dt className="text-sm text-muted-foreground">{k}</dt>
                            <dd className="text-sm font-semibold text-foreground text-right">{v}</dd>
                          </div>
                        ))}
                      </dl>
                    </section>
                  </div>
                </TabsContent>

                <TabsContent value="bracket" className="mt-0">
                  {bracket?.structure ? (
                    tournament.format === 'double_elimination' ? (
                      <DoubleBracketView
                        winners={bracket.structure.winners || []}
                        losers={bracket.structure.losers || []}
                        finals={bracket.structure.finals || []}
                        onMatchClick={undefined}
                        editable={false}
                      />
                    ) : (
                      <BracketView
                        rounds={bracket.structure.winners || []}
                        onMatchClick={undefined}
                        editable={false}
                        format={tournament.format}
                      />
                    )
                  ) : (
                    <EmptyPanel
                      icon={LayoutGrid}
                      title="Bracket no disponible"
                      description="Se publicará cuando el torneo comience"
                    />
                  )}
                </TabsContent>

                <TabsContent value="teams" className="mt-0">
                  <TournamentTeamsList
                    teams={teams}
                    variant="public"
                    emptyDescription="Sé el primero en inscribir tu equipo"
                    gridClassName="grid grid-cols-1 md:grid-cols-2 gap-4"
                  />
                </TabsContent>

                <TabsContent value="matches" className="mt-0">
                  <MatchesTab
                    matches={bracket?.matches || []}
                    teams={bracket?.teams || []}
                    onMatchClick={setSelectedMatch}
                  />
                </TabsContent>

                {tournament.game_type === 'valorant' && !isDraftPreview && (
                  <TabsContent value="stats" className="mt-0">
                    <TournamentStatsTab
                      tournamentId={tournament.id}
                      slugOrId={tournament.slug || tournament.id}
                      isPublic
                    />
                  </TabsContent>
                )}
              </div>
            </Tabs>
          </div>

          {/* Sidebar sticky */}
          <aside className="lg:sticky lg:top-24 lg:self-start space-y-4">
            <div className="panel-solid rounded-2xl p-6 glow-border">
              <h2 className="font-display text-lg font-bold text-foreground mb-4">Inscripción</h2>

              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Cupos</span>
                  <span className="font-display font-bold text-primary">
                    {tournament.current_teams}/{tournament.max_teams}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${teamFillPercent}%` }}
                  />
                </div>
              </div>

              {canRegister() ? (
                <Button
                  onClick={handleRegisterClick}
                  size="lg"
                  className="w-full glow-primary font-semibold"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Inscribir equipo
                </Button>
              ) : (
                <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-center text-sm text-muted-foreground">
                  {tournament.status !== 'registration'
                    ? 'Inscripciones cerradas'
                    : tournament.current_teams >= tournament.max_teams
                      ? 'Cupos agotados'
                      : 'Plazo de inscripción vencido'}
                </div>
              )}

              {tournament.prize_pool != null && Number(tournament.prize_pool) > 0 && (
                <div className="mt-6 pt-6 border-t border-primary/15 text-center">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Premio total</p>
                  <p className="font-display text-3xl font-bold text-gradient-accent">
                    {formatPrizePool(tournament.prize_pool, tournament.prize_pool_currency)}
                  </p>
                </div>
              )}

              <PrizeDistributionDisplay
                distribution={tournament.prize_distribution}
                totalPrize={tournament.prize_pool}
                currency={tournament.prize_pool_currency}
                className="mt-6 pt-6 border-t border-primary/15"
              />
            </div>

            <TournamentSocialLinksDisplay
              links={tournament.social_links}
              className="panel-solid rounded-2xl p-5"
            />

            <div className="panel-solid rounded-2xl p-5 space-y-3 text-sm">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Resumen</p>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Estado</span>
                <span className="font-medium text-foreground">
                  {TOURNAMENT_STATUS_LABELS[tournament.status]}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Partidas</span>
                <span className="font-medium text-foreground">{bracket?.matches?.length ?? 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Equipos</span>
                <span className="font-medium text-foreground">{teams.length}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <PublicRegisterTeam
        isOpen={isRegisterModalOpen}
        tournamentId={tournament.id}
        tournamentSlug={tournament.slug}
        gameType={tournament.game_type}
        onClose={() => setIsRegisterModalOpen(false)}
        onSuccess={loadTournamentData}
      />

      {selectedMatch && (
        <MatchStatsModal
          match={selectedMatch}
          teams={bracket?.teams || teams}
          slugOrId={tournament.slug || tournament.id}
          onClose={() => setSelectedMatch(null)}
        />
      )}
    </TournamentPublicLayout>
  );
};

function EmptyPanel({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-primary/25 bg-card/40 px-8 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-7 w-7" />
      </div>
      <p className="font-display text-lg font-semibold text-foreground">{title}</p>
      <p className="text-sm text-muted-foreground max-w-xs">{description}</p>
    </div>
  );
}

const MatchesTab: React.FC<{
  matches: TournamentMatch[];
  teams: TournamentTeam[];
  onMatchClick?: (match: TournamentMatch) => void;
}> = ({ matches, teams, onMatchClick }) => {
  if (!matches.length) {
    return (
      <EmptyPanel
        icon={Swords}
        title="No hay partidas aún"
        description="Las partidas aparecerán cuando el torneo comience"
      />
    );
  }

  const getTeamName = (teamId: string | undefined): string => {
    if (!teamId) return 'TBD';
    return teams.find((t) => t.id === teamId)?.team_name || 'TBD';
  };

  return (
    <div className="space-y-3">
      {matches.map((match) => (
        <div
          key={match.id}
          {...(onMatchClick ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: () => onMatchClick(match),
            onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') onMatchClick(match); },
          } : {})}
          className={cn(
            'rounded-xl border border-primary/15 bg-card/60 p-4 transition-colors',
            onMatchClick && 'cursor-pointer hover:border-primary/30 hover:bg-card/80',
          )}
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="border-primary/25">
                R{match.round} · M{match.match_number}
              </Badge>
              <Badge variant="outline" className={getMatchStatusColor(match.status)}>
                {MATCH_STATUS_LABELS[match.status]}
              </Badge>
            </div>
            {match.scheduled_time && (
              <span className="text-xs text-muted-foreground">
                {new Date(match.scheduled_time).toLocaleString('es-ES')}
              </span>
            )}
          </div>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <MatchTeamSide
              name={getTeamName(match.team1_id)}
              score={match.team1_score}
              logo={teams.find((t) => t.id === match.team1_id)?.logo_url}
              align="left"
            />
            <span className="font-display text-sm font-bold text-muted-foreground">VS</span>
            <MatchTeamSide
              name={getTeamName(match.team2_id)}
              score={match.team2_score}
              logo={teams.find((t) => t.id === match.team2_id)?.logo_url}
              align="right"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

function MatchTeamSide({
  name,
  score,
  logo,
  align,
}: {
  name: string;
  score: number | null | undefined;
  logo?: string | null;
  align: 'left' | 'right';
}) {
  const isRight = align === 'right';
  return (
    <div className={cn('flex items-center gap-2', isRight && 'flex-row-reverse text-right')}>
      {logo ? (
        <img 
          src={logo} 
          alt="" 
          width={36}
          height={36}
          loading="lazy"
          className="h-9 w-9 rounded-full border border-primary/20 object-cover" 
        />
      ) : (
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
          <Users className="h-4 w-4 text-primary/60" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-foreground text-sm">{name}</p>
        {score != null && (
          <p className="font-display text-xl font-bold text-primary">{score}</p>
        )}
      </div>
    </div>
  );
}

const getMatchStatusColor = (status: TournamentMatch['status']): string => {
  const colors = {
    pending: 'bg-muted/80 text-muted-foreground border-border',
    waiting: 'bg-muted/50 text-muted-foreground/60 border-border/50',
    in_progress: 'animate-glow-pulse bg-primary/20 text-primary border-primary/40',
    completed: 'bg-primary/10 text-primary border-primary/25',
    walkover: 'bg-muted/80 text-muted-foreground border-border',
    disputed: 'bg-destructive/20 text-destructive border-destructive/40',
  };
  return colors[status] || colors.pending;
};
